import {
  ActionIcon,
  Alert,
  Box,
  Button,
  Center,
  CopyButton,
  Grid,
  Group,
  LoadingOverlay,
  Paper,
  rem,
  Stack,
  Text,
  TextInput,
  Tooltip,
} from '@mantine/core';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { PiCopySimpleLight, PiLock, PiWarning } from 'react-icons/pi';
import { call } from '@/lib/api';
import { isKycDenied, KYC_NO_ACCESS } from '@/lib/kycAccess';
import { TContact } from '@/models/contacts';
import { Document } from '@/types';
import Loader from '../Loader';
import KYCDocuments from './KYCDocuments';
import { KYCInfo } from './types';

export default function KYC({ contact }: { contact: TContact }) {
  const [kycInfo, setKYCInfo] = useState<KYCInfo | null>(null);
  const [documents, setDocuments] = useState<Document[] | null>(null);

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [denied, setDenied] = useState(false);

  useEffect(() => {
    call<{ info: KYCInfo[]; documents: Document[] }>('contact/kyc', {
      json: { pulseId: contact.id },
    })
      .then((res) => res.json())
      .then(({ info, documents }) => {
        if (info) {
          setKYCInfo(info[0]);
        }
        if (documents) {
          setDocuments(documents);
        }
      })
      .catch((e) => {
        if (isKycDenied(e)) setDenied(true);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [contact]);

  if (loading) {
    return <Loader />;
  }

  if (denied) {
    return (
      <Center p="md">
        <Alert color="red" icon={<PiLock size="3em" />} maw={600}>
          {KYC_NO_ACCESS}
        </Alert>
      </Center>
    );
  }

  if (!kycInfo) {
    return (
      <Center p="md">
        <Alert title="No connection" icon={<PiWarning size="3em" />} maw={600}>
          <Box mb="md">
            The contact has not been connected with Backoffice yet. The contact
            exists in Monday, but in order to operate with it in Backoffice,
            he/she has to be connected. Normally, this is done automatically,
            but for older contacts, a manual connection may be necessary.
          </Box>
          <Group justify="flex-end">
            <Button
              maw={150}
              onClick={() => {
                call<void>('contact/kyc-init', {
                  json: { pulseId: contact.id },
                })
                  .then(() => {
                    setKYCInfo({ pulseId: contact.id } as KYCInfo);
                  })
                  .catch((e) => {
                    if (isKycDenied(e)) setDenied(true);
                  });
              }}
            >
              Connect now
            </Button>
          </Group>
        </Alert>
      </Center>
    );
  }

  return (
    <Grid my="md" maw="960px">
      <Grid.Col span={6}>
        <Paper p="md" shadow="xs" pos="relative" h="100%">
          <LoadingOverlay
            visible={submitting}
            loaderProps={{ children: <Loader /> }}
          />
          <Stack>
            <Text size="md" fw={700}>
              SumSub WebURL
            </Text>
            <TextInput
              variant="filled"
              labelProps={{ pb: 'sm' }}
              readOnly={true}
              value={kycInfo.sumsubUrl ?? 'Not generated'}
              disabled={!kycInfo.sumsubUrl}
              description={
                kycInfo.sumsubUrlExpiresAt
                  ? `Expires on ${dayjs(kycInfo.sumsubUrlExpiresAt)
                      .format('DD.MM.YYYY HH:mm')
                      .split(' ')
                      .join(' at ')}`
                  : null
              }
              rightSection={
                kycInfo?.sumsubUrl && (
                  <CopyButton value={kycInfo?.sumsubUrl} timeout={2000}>
                    {({ copied, copy }) => (
                      <Tooltip
                        label={copied ? 'Copied' : 'Copy'}
                        withArrow
                        position="right"
                      >
                        <ActionIcon
                          color={copied ? 'teal' : 'gray'}
                          variant="subtle"
                          onClick={copy}
                        >
                          {copied ? (
                            <PiCopySimpleLight style={{ width: rem(16) }} />
                          ) : (
                            <PiCopySimpleLight style={{ width: rem(16) }} />
                          )}
                        </ActionIcon>
                      </Tooltip>
                    )}
                  </CopyButton>
                )
              }
            />
            <Group justify="flex-end">
              <Button
                size="xs"
                bg="blue.1"
                onClick={() => {
                  setSubmitting(true);
                  call<Pick<KYCInfo, 'sumsubUrl' | 'sumsubUrlExpiresAt'>>(
                    `contact/kyc-generate-link`,
                    {
                      json: { pulseId: contact.id },
                    },
                  )
                    .then((res) => res.json())
                    .then((upd) => setKYCInfo({ ...kycInfo, ...upd }))
                    .catch((e) => {
                      if (isKycDenied(e)) setDenied(true);
                    })
                    .finally(() => setSubmitting(false));
                }}
              >
                {kycInfo.sumsubUrl ? 'Re-g' : 'G'}enerate
              </Button>
            </Group>
          </Stack>
        </Paper>
      </Grid.Col>
      <Grid.Col span={6}>
        <Paper p="md" shadow="xs" h={'100%'}>
          <KYCDocuments kycInfo={kycInfo} documents={documents} />
        </Paper>
      </Grid.Col>
    </Grid>
  );
}
